/*
 * Copyright (c) 2017-present, flop! contributors
 * SPDX-License-Identifier: AGPL-3.0-or-later
 * https://framagit.org/flopedt/flopedt
 */

import * as v from 'valibot'
import { createI18n } from 'vue-i18n'

import { DEFAULT_LOCALE, defaultMessages } from './messages.ts'

// Structure inspired by article from Lokalise - https://lokalise.com/blog/vue-i18n/
// It itself takes most of its info from the vue-i18n docs

// Export i18n
export const i18n = createI18n<[typeof defaultMessages], string, false>({
  legacy: false,
  locale: DEFAULT_LOCALE, // Will be updated by settings.ts#initSettings.
  fallbackLocale: DEFAULT_LOCALE,
  // Only the default locale is loaded other locales are lazy-loaded for performance and bundle size concerns.
  messages: { [DEFAULT_LOCALE]: defaultMessages },
})

// Export some helpers
export function formatList(list: Iterable<string>, options: Intl.ListFormatOptions) {
  const formatter = new Intl.ListFormat(i18n.global.locale.value, options)
  return formatter.format(list)
}

export function formatBytes(bytes: number): string {
  const units = ['byte', 'kilobyte', 'megabyte', 'gigabyte', 'terabyte']

  const navigatorLocal = navigator.languages && navigator.languages.length >= 0 ? navigator.languages[0] : 'en-US'
  const unitIndex = Math.max(0, Math.min(Math.floor(Math.log(bytes) / Math.log(1000)), units.length - 1))

  return Intl.NumberFormat(navigatorLocal, {
    style: 'unit',
    unit: units[unitIndex],
  }).format(bytes / 1000 ** unitIndex)
}

// Configure Valibot
const TYPES = [v.string, v.number, v.boolean, v.file]
const REQUIRED = [v.nonOptional, v.nonEmpty]
const $t = i18n.global.t

REQUIRED.forEach((e) => v.setSpecificMessage(e, () => $t('valibot.required')))
TYPES.forEach((e) =>
  v.setSpecificMessage(e, (i) =>
    $t('valibot.invalid-type', {
      expected: i18n.global.t(`valibot.type.${i.expected}`, i.expected),
      received: i18n.global.t(`valibot.type.${i.received}`, i.received),
    })
  )
)

v.setSpecificMessage(v.email, () => $t('valibot.email'))
v.setSpecificMessage(v.url, () => $t('valibot.url'))

v.setSpecificMessage(v.minLength, ({ requirement }) => $t('valibot.min-length', { requirement }))
v.setSpecificMessage(v.maxLength, ({ requirement }) => $t('valibot.max-length', { requirement }))

// Named this way so it's picked up by i18n ally
const makeValue$t =
  (msg: string) =>
  ({ requirement }: { requirement: v.ValueInput }) =>
    requirement instanceof Date
      ? $t(`${msg}-date`, { requirement: i18n.global.d(requirement) })
      : $t(msg, { requirement })

v.setSpecificMessage(v.minValue, makeValue$t('valibot.min-value'))
v.setSpecificMessage(v.maxValue, makeValue$t('valibot.max-value'))

v.setSpecificMessage(v.gtValue, makeValue$t('valibot.gt-value'))
v.setSpecificMessage(v.ltValue, makeValue$t('valibot.lt-value'))

v.setSpecificMessage(v.picklist, () => $t('valibot.not-in-picklist'))
v.setSpecificMessage(v.minEntries, ({ requirement }) => $t('valibot.min-entries', { requirement }))
v.setSpecificMessage(v.maxEntries, ({ requirement }) => $t('valibot.max-entries', { requirement }))

v.setSpecificMessage(v.mimeType, ({ requirement }) =>
  $t('valibot.mime-type', { requirement: formatList(requirement, { style: 'long', type: 'disjunction' }) })
)
v.setSpecificMessage(v.minSize, ({ input, requirement }) =>
  input instanceof Blob
    ? $t('valibot.min-file-size', {
        requirement: formatBytes(requirement),
      })
    : $t('valibot.min-entries', { requirement })
)
v.setSpecificMessage(v.maxSize, ({ input, requirement }) =>
  input instanceof Blob
    ? $t('valibot.max-file-size', {
        requirement: formatBytes(requirement),
      })
    : $t('valibot.max-entries', { requirement })
)
